import type { Permission, SessionUser } from '@canopee/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { apiRequest } from '../api/client.js';
import { broadcastLogout } from '../auth/session.js';
import {
  NAVIGATION_AUTHORITY,
  navigationGroupsForItems,
  navigationItemsForPermissions,
} from './navigation.js';
import styles from './app-layout.module.css';

interface AppLayoutProperties {
  readonly user: SessionUser;
  readonly permissions: readonly Permission[];
}

export function AppLayout({ user, permissions }: AppLayoutProperties) {
  const items = navigationItemsForPermissions(permissions);
  const groups = navigationGroupsForItems(items);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel('canopee-auth');
    channel.onmessage = (event: MessageEvent<unknown>) => {
      if (
        typeof event.data === 'object' &&
        event.data !== null &&
        'type' in event.data &&
        event.data.type === 'logout'
      ) {
        queryClient.clear();
        void navigate('/login', { replace: true });
      }
    };
    return () => {
      channel.close();
    };
  }, [navigate, queryClient]);
  const logout = useMutation({
    mutationFn: () =>
      apiRequest('/api/v1/auth/logout', z.object({ loggedOut: z.literal(true) }), {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => {
      queryClient.clear();
      broadcastLogout();
      void navigate('/login', { replace: true });
    },
  });

  return (
    <div className={styles.layout}>
      <aside className={styles.sidebar}>
        <div className={styles.brand}>Canopée</div>
        <nav
          aria-label="Navigation principale"
          className={styles.navigation}
          data-navigation-renderer={NAVIGATION_AUTHORITY.rendererId}
        >
          {groups.map((group) => (
            <section className={styles.navigationGroup} key={group}>
              <h2>{group}</h2>
              {items
                .filter((item) => item.group === group)
                .map((item) => (
                  <NavLink
                    className={({ isActive }) => (isActive ? styles.activeLink : styles.link)}
                    end={item.path === '/'}
                    key={item.path}
                    to={item.path}
                    data-navigation-path={item.path}
                  >
                    {item.label}
                  </NavLink>
                ))}
            </section>
          ))}
        </nav>
        <div className={styles.account}>
          <span>{user.displayName}</span>
          <span className={styles.role}>{user.role}</span>
          <button
            className={styles.logout}
            disabled={logout.isPending}
            onClick={() => {
              logout.mutate();
            }}
            type="button"
          >
            Déconnexion
          </button>
          {logout.isError ? <span role="alert">{logout.error.message}</span> : null}
        </div>
      </aside>
      <main className={styles.content} id="main-content">
        <Outlet />
      </main>
    </div>
  );
}
